import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { CalendarCheck, ChevronLeft, AngleRight } from 'theme/icon';
import * as actions from 'redux/actions/calendarActions';
import { getApiHeaders } from 'api/apiHeaders';
import { config } from 'config';
import 'assets/css/market-calendar.css';

function pad2(n) {
    return String(n).padStart(2, '0');
}

function toYMD(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function normalizeDateAt(value) {
    if (!value) {
        return '';
    }
    const raw = String(value);
    return raw.includes('T') ? raw.split('T')[0] : raw.slice(0, 10);
}

function startOfMonth(year, month) {
    return new Date(year, month, 1);
}

/** Monday-first month grid (6 weeks). */
function buildMonthCells(year, month) {
    const first = startOfMonth(year, month);
    const mondayOffset = (first.getDay() + 6) % 7;
    const gridStart = new Date(year, month, 1 - mondayOffset);
    const cells = [];
    for (let i = 0; i < 42; i += 1) {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        cells.push({
            key: toYMD(d),
            date: d,
            day: d.getDate(),
            inMonth: d.getMonth() === month,
        });
    }
    return cells;
}

function monthLabel(year, month, locale) {
    const d = new Date(year, month, 1);
    const label = d.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', {
        month: 'long',
        year: 'numeric',
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
}

const WEEKDAYS_ES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const WEEKDAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MAX_EVENTS_PER_DAY = 5;

const MarketCalendar = (props) => {
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const todayStr = toYMD(today);

    const [cursor, setCursor] = useState(() => ({
        year: today.getFullYear(),
        month: today.getMonth(),
    }));
    const [earnings, setEarnings] = useState([]);
    const [earningCounts, setEarningCounts] = useState({});

    const cells = useMemo(
        () => buildMonthCells(cursor.year, cursor.month),
        [cursor.year, cursor.month],
    );

    const rangeFrom = cells[0]?.key;
    const rangeTo = cells[cells.length - 1]?.key;

    useEffect(() => {
        if (!props.sessionReady || !props.user_id || !rangeFrom || !rangeTo) {
            return;
        }
        props.loadList({
            lang: props.lang,
            from: rangeFrom,
            to: rangeTo,
            size: 200,
            index: 0,
        });
    }, [props.sessionReady, props.user_id, props.lang, rangeFrom, rangeTo]);

    useEffect(() => {
        if (!props.sessionReady || !props.user_id || !rangeFrom || !rangeTo) {
            return undefined;
        }

        let cancelled = false;
        axios
            .get(`${config.api.web}discover/earning`, {
                params: {
                    from: rangeFrom,
                    to: rangeTo,
                    per_day: MAX_EVENTS_PER_DAY,
                },
                headers: getApiHeaders(),
            })
            .then((response) => {
                if (cancelled) return;
                if (response.status < 300 && response.data?.status === 'Success') {
                    setEarnings(Array.isArray(response.data.body?.list) ? response.data.body.list : []);
                    setEarningCounts(response.data.body?.counts || {});
                } else {
                    setEarnings([]);
                    setEarningCounts({});
                }
            })
            .catch(() => {
                if (cancelled) return;
                setEarnings([]);
                setEarningCounts({});
            });

        return () => {
            cancelled = true;
        };
    }, [props.sessionReady, props.user_id, rangeFrom, rangeTo]);

    const holidays = Array.isArray(props.list) ? props.list : [];

    const eventsByDate = useMemo(() => {
        const map = {};
        const push = (ymd, event) => {
            if (!ymd) return;
            if (!map[ymd]) map[ymd] = [];
            map[ymd].push(event);
        };

        holidays.forEach((item) => {
            const ymd = normalizeDateAt(item.date_at);
            const name = item[`name_${props.lang}`]
                || item.name_es
                || item.name_en
                || '';
            push(ymd, {
                id: `holiday-${item.id}`,
                type: 'holiday',
                label: name,
                tone: 'holiday',
            });
        });

        earnings.forEach((item) => {
            const ymd = normalizeDateAt(item.earning_at || item.date_at);
            push(ymd, {
                id: `earning-${item.symbol}-${ymd}-${item.hour || ''}`,
                type: 'earning',
                label: item.symbol,
                tone: item.tone || 'earning',
                symbol: item.symbol,
                iconUrl: item.icon_url || item.logo_url,
            });
        });

        return map;
    }, [holidays, earnings, props.lang]);

    const goPrev = () => {
        setCursor((prev) => {
            const d = new Date(prev.year, prev.month - 1, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
        });
    };

    const goNext = () => {
        setCursor((prev) => {
            const d = new Date(prev.year, prev.month + 1, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
        });
    };

    const goToday = () => {
        setCursor({ year: today.getFullYear(), month: today.getMonth() });
    };

    const weekdays = props.lang === 'es' ? WEEKDAYS_ES : WEEKDAYS_EN;

    return (
        <div className="market-calendar-page">
            <div className="market-calendar-header">
                <div>
                    <h1 className="market-calendar-title">
                        <Translate value="calendar_title" />
                    </h1>
                    <p className="market-calendar-subtitle">
                        <Translate value="market_calendar_subtitle" />
                    </p>
                </div>
                <div className="market-calendar-nav">
                    <button type="button" className="market-calendar-nav-btn" onClick={goPrev} aria-label="Previous month">
                        <ChevronLeft />
                    </button>
                    <button type="button" className="market-calendar-month-btn" onClick={goToday}>
                        {monthLabel(cursor.year, cursor.month, props.lang)}
                    </button>
                    <button type="button" className="market-calendar-nav-btn" onClick={goNext} aria-label="Next month">
                        <AngleRight />
                    </button>
                </div>
            </div>

            <div className="market-calendar-card">
                <div className="market-calendar-weekdays" role="row">
                    {weekdays.map((label) => (
                        <div key={label} className="market-calendar-weekday" role="columnheader">
                            {label}
                        </div>
                    ))}
                </div>

                <div className="market-calendar-grid" role="grid">
                    {cells.map((cell) => {
                        const dayEvents = eventsByDate[cell.key] || [];
                        const events = dayEvents.slice(0, MAX_EVENTS_PER_DAY);
                        // The API already caps earnings per day, so the real total comes from `counts`.
                        const holidayCount = dayEvents.filter((ev) => ev.type === 'holiday').length;
                        const totalEarnings = earningCounts[cell.key] != null
                            ? earningCounts[cell.key]
                            : dayEvents.length - holidayCount;
                        const hiddenCount = (holidayCount + totalEarnings) - events.length;
                        const isToday = cell.key === todayStr;
                        return (
                            <div
                                key={cell.key}
                                className={[
                                    'market-calendar-cell',
                                    cell.inMonth ? '' : 'is-outside',
                                    isToday ? 'is-today' : '',
                                ].filter(Boolean).join(' ')}
                                role="gridcell"
                            >
                                <span className="market-calendar-daynum">{cell.day}</span>
                                <div className="market-calendar-events">
                                    {events.map((ev) => {
                                        const pillContent = (
                                            <>
                                                {ev.type === 'holiday' ? (
                                                    <span className="market-calendar-pill-icon" aria-hidden="true">
                                                        <CalendarCheck />
                                                    </span>
                                                ) : ev.iconUrl ? (
                                                    <img
                                                        className="market-calendar-pill-avatar"
                                                        src={ev.iconUrl}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <span className="market-calendar-pill-dot" aria-hidden="true" />
                                                )}
                                                <span className="market-calendar-pill-label">{ev.label}</span>
                                            </>
                                        );

                                        if (ev.type === 'earning' && ev.symbol) {
                                            return (
                                                <RouterLink
                                                    key={ev.id}
                                                    to={`/stock/${ev.symbol}`}
                                                    className={`market-calendar-pill market-calendar-pill--${ev.tone} market-calendar-pill--link`}
                                                    title={ev.label}
                                                >
                                                    {pillContent}
                                                </RouterLink>
                                            );
                                        }

                                        return (
                                            <div
                                                key={ev.id}
                                                className={`market-calendar-pill market-calendar-pill--${ev.tone}`}
                                                title={ev.label}
                                            >
                                                {pillContent}
                                            </div>
                                        );
                                    })}
                                    {hiddenCount > 0 && (
                                        <RouterLink
                                            to="/discover"
                                            className="market-calendar-more"
                                            title={I18n.t('market_calendar_more_events', { count: hiddenCount })}
                                        >
                                            {I18n.t('market_calendar_more_events', { count: hiddenCount })}
                                        </RouterLink>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {props.loading && (
                    <p className="market-calendar-status">
                        <Translate value="report_loading" />
                    </p>
                )}
            </div>

            <div className="market-calendar-legend">
                <span className="market-calendar-legend-item">
                    <span className="market-calendar-pill market-calendar-pill--holiday market-calendar-pill--sample">
                        <CalendarCheck />
                        <span><Translate value="market_calendar_legend_holiday" /></span>
                    </span>
                </span>
                <span className="market-calendar-legend-item">
                    <span className="market-calendar-pill market-calendar-pill--earning market-calendar-pill--sample">
                        <span className="market-calendar-pill-dot" aria-hidden="true" />
                        <span><Translate value="market_calendar_legend_earnings" /></span>
                    </span>
                </span>
            </div>
        </div>
    );
};

MarketCalendar.propTypes = {
    user_id: PropTypes.string,
    sessionReady: PropTypes.bool,
    list: PropTypes.array,
    loading: PropTypes.bool,
    lang: PropTypes.string,
    loadList: PropTypes.func.isRequired,
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        sessionReady: reducer.session.sessionReady,
        list: reducer.calendar.list,
        loading: reducer.calendar.loading,
        lang: reducer.i18n.locale,
    };
}

const mapDispatchToProps = (dispatch) => ({
    loadList: (params) => dispatch(actions.loadList({ params })),
});

export default connect(mapStateToProps, mapDispatchToProps)(MarketCalendar);
